import React, { useState, useEffect, useCallback } from 'react';
import { audioService, adminService, naatKhawanService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { AudioFile, NaatKhawan } from '../../types';
import { getApiUrl, getFetchOptions } from '../../config/environment';
import Pagination from '../common/Pagination';
import ConfirmationModal from '../common/ConfirmationModal';
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  Upload,
  X,
  Save,
  Link as LinkIcon,
  Music,
  CloudDownload
} from 'lucide-react';
import './AdminNaats.css';

interface AudioForm {
  title: string;
  urduTitle: string;
  naatKhawanId: number;
  duration: string;
  playCount: number;
  filePath: string;
  lyrics?: string;
  file?: File;
}

const AdminNaats: React.FC = () => {
  const [audios, setAudios] = useState<AudioFile[]>([]);
  const [naatKhawans, setNaatKhawans] = useState<NaatKhawan[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedAudio, setSelectedAudio] = useState<AudioFile | null>(null);
  const [audioForm, setAudioForm] = useState<AudioForm>({
    title: '',
    urduTitle: '',
    naatKhawanId: 0,
    duration: '',
    playCount: 0,
    filePath: '',
    lyrics: '',
  });
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url' | 'fetch'>('file');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [audioToDelete, setAudioToDelete] = useState<AudioFile | null>(null);
  const [translating, setTranslating] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const { showToast } = useToast();

  // Load data on component mount
  const loadAudios = useCallback(async () => {
    setLoading(true);
    try {
      const response = await audioService.getAll(currentPage, 20, searchTerm);
      if (response.success && response.data) {
        setAudios(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      showToast('Failed to load naats', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [currentPage, searchTerm, showToast]);

  useEffect(() => {
    loadAudios();
    loadNaatKhawans();
  }, [loadAudios]);

  const loadNaatKhawans = async () => {
    try {
      const response = await naatKhawanService.getAlphabetical();
      if (response.success && response.data) {
        setNaatKhawans(response.data);
      }
    } catch (error) {
      console.error('Failed to load NaatKhawans:', error);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const openAddModal = () => {
    setModalMode('add');
    setAudioForm({
      title: '',
      urduTitle: '',
      naatKhawanId: 0,
      duration: '',
      playCount: 0,
      filePath: '',
      lyrics: '',
      file: undefined,
    });
    setSelectedAudio(null);
    setUploadMethod('file');
    setShowModal(true);
  };

  const openEditModal = (audio: AudioFile) => {
    setModalMode('edit');
    setSelectedAudio(audio);
    setAudioForm({
      title: audio.title,
      urduTitle: audio.urduTitle || '',
      naatKhawanId: audio.naatKhawanId,
      duration: typeof audio.duration === 'number' ? audio.duration.toString() : (audio.duration || ''),
      playCount: audio.playCount || 0,
      filePath: audio.filePath,
      lyrics: audio.lyrics || '',
    });
    setUploadMethod('url');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAudio(null);
    setAudioForm({
      title: '',
      urduTitle: '',
      naatKhawanId: 0,
      duration: '',
      playCount: 0,
      filePath: '',
      lyrics: '',
      file: undefined,
    });
    setUploadMethod('file');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!audioForm.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }

    if (!audioForm.naatKhawanId || audioForm.naatKhawanId === 0) {
      showToast('Please select a Naat Khawan', 'error');
      return;
    }

    if ((uploadMethod === 'url' || uploadMethod === 'fetch') && !audioForm.filePath.trim()) {
      showToast('Audio file URL is required', 'error');
      return;
    }

    if (uploadMethod === 'file' && !audioForm.file) {
      showToast('Please select an audio file', 'error');
      return;
    }

    // Validate URL for fetch method
    if (uploadMethod === 'fetch' && !audioForm.filePath.startsWith('http')) {
      showToast('Please provide a valid URL starting with http:// or https://', 'error');
      return;
    }

    setLoading(true);

    // Set loading message based on upload method
    if (uploadMethod === 'fetch') {
      setLoadingMessage('Downloading audio file from URL... This may take a few moments.');
    } else if (uploadMethod === 'file') {
      setLoadingMessage('Uploading audio file...');
    } else {
      setLoadingMessage('Processing...');
    }

    try {
      if (modalMode === 'add') {
        const formData = new FormData();
        formData.append('title', audioForm.title);
        formData.append('urduTitle', audioForm.urduTitle || '');
        formData.append('naatKhawanId', audioForm.naatKhawanId.toString());
        formData.append('duration', audioForm.duration || '0');
        formData.append('playCount', audioForm.playCount.toString());
        formData.append('lyrics', audioForm.lyrics || '');

        if (uploadMethod === 'file' && audioForm.file) {
          formData.append('audioFile', audioForm.file);
          formData.append('uploadType', 'file');
          console.log('Uploading file:', audioForm.file.name, 'Size:', audioForm.file.size);
        } else if (uploadMethod === 'fetch') {
          // For fetch method, send the URL to backend to download
          formData.append('audioUrl', audioForm.filePath);
          formData.append('uploadType', 'fetch');
          console.log('Fetching from URL:', audioForm.filePath);
        } else {
          // For URL method, just save the path
          formData.append('filePath', audioForm.filePath);
          formData.append('uploadType', 'url');
          console.log('Using URL/Path:', audioForm.filePath);
        }

        // Debug: Log all FormData entries
        console.log('FormData being sent:');
        formData.forEach((value, key) => {
          console.log(`${key}:`, value);
        });

        // Try using fetch directly to test if it's an axios issue
        let response;
        if ((uploadMethod === 'file' && audioForm.file) || uploadMethod === 'fetch') {
          console.log('Using fetch for file upload or URL fetch');
          const fetchResponse = await fetch(getApiUrl('/admin/audios'), {
            method: 'POST',
            body: formData,
            ...getFetchOptions(),
            // Don't set Content-Type header, let browser set it with boundary
          });
          const fetchData = await fetchResponse.json();
          console.log('Fetch response:', fetchData);

          // Check for specific error messages
          if (!fetchResponse.ok || !fetchData.success) {
            if (fetchData.error) {
              console.error('Upload error:', fetchData.error);
              if (fetchData.php_limits) {
                console.error('PHP Limits:', fetchData.php_limits);
              }
            }
          }
          response = fetchData;
        } else {
          response = await adminService.createAudio(formData);
        }
        
        if (response.success) {
          showToast('Naat added successfully!', 'success');
          loadAudios();
          closeModal();
        } else {
          showToast('Add failed: ' + (response.error || 'Unknown error'), 'error');
        }
      } else if (selectedAudio) {
        const updateData = {
          title: audioForm.title,
          urduTitle: audioForm.urduTitle,
          naatKhawanId: audioForm.naatKhawanId,
          duration: parseFloat(audioForm.duration) || 0,
          playCount: audioForm.playCount,
          filePath: audioForm.filePath,
          lyrics: audioForm.lyrics || '',
        };

        const response = await adminService.updateAudio(selectedAudio.id, updateData);
        if (response.success) {
          showToast('Naat updated successfully!', 'success');
          loadAudios();
          closeModal();
        } else {
          showToast('Update failed: ' + (response.error || 'Unknown error'), 'error');
        }
      }
    } catch (error) {
      showToast(`Failed to ${modalMode} naat: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleDelete = async (audio: AudioFile) => {
    setAudioToDelete(audio);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!audioToDelete) return;
    
    try {
      const response = await adminService.deleteAudio(audioToDelete.id);
      if (response.success) {
        showToast('Naat deleted successfully!', 'success');
        loadAudios();
      }
    } catch (error) {
      showToast('Failed to delete naat', 'error');
    } finally {
      setAudioToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setAudioToDelete(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);
      setAudioForm(prev => ({ ...prev, file }));
    } else {
      console.log('No file selected');
    }
  };

  // Transcribe audio to Urdu lyrics
  const handleTranscribe = async () => {
    if (!audioForm.filePath) {
      showToast('Please provide an audio URL first', 'error');
      return;
    }

    setTranscribing(true);
    try {
      const response = await fetch(getApiUrl('/transcribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audioUrl: audioForm.filePath
        }),
        ...getFetchOptions(),
      });

      const data = await response.json();

      if (data.success && data.transcription) {
        setAudioForm(prev => ({ ...prev, lyrics: data.transcription }));
        showToast(data.message || 'Audio transcribed successfully!', 'success');
      } else {
        showToast(`Transcription failed: ${data.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      showToast('Transcription failed. Please check your connection and try again.', 'error');
    } finally {
      setTranscribing(false);
    }
  };

  // Auto-translate English title to Urdu using Google Translate API via backend
  const handleAutoTranslate = async () => {
    if (!audioForm.title.trim()) return;
    
    setTranslating(true);
    try {
      
      // Call our backend translation endpoint
      const response = await fetch(getApiUrl('/admin/translate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: audioForm.title.trim(),
          from: 'en',
          to: 'ur'
        }),
        ...getFetchOptions(),
      });
      
      const data = await response.json();
      
      if (data.success && data.translatedText) {
        // Set the translated title
        setAudioForm(prev => ({ ...prev, urduTitle: data.translatedText }));
        showToast('Title translated successfully using Google Translate!', 'success');
      } else {
        showToast(`Translation failed: ${data.error || 'Unknown error'}`, 'error');
      }
      
    } catch (error) {
      console.error('Translation error:', error);
      showToast('Translation failed. Please check your internet connection and try again.', 'error');
    } finally {
      setTranslating(false);
    }
  };

  const getNaatKhawanName = (id: number) => {
    const naatKhawan = naatKhawans.find(nk => nk.id === id);
    return naatKhawan?.name || 'Unknown';
  };

  return (
    <div className="admin-naats">
      <div className="admin-naats-header">
        <h2 className="admin-section-title">
          <Music size={24} />
          Manage Naats
        </h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={openAddModal}
        >
          <Plus size={20} />
          Add New Naat
        </button>
      </div>

      <div className="admin-search-bar">
        <div className="admin-search-input-wrapper">
          <Search className="admin-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search naats..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="admin-search-input"
          />
        </div>
      </div>

      {loading && (
        <div className="admin-loading">Loading naats...</div>
      )}

      <div className="admin-naats-table">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Urdu Title</th>
              <th>NaatKhawan</th>
              <th>Duration</th>
              <th>Play Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {audios.map((audio) => (
              <tr key={audio.id}>
                <td>
                  <div className="admin-audio-title">
                    <strong>{audio.title}</strong>
                  </div>
                </td>
                <td>
                  <div className="admin-urdu-title" dir="rtl">
                    {audio.urduTitle}
                  </div>
                </td>
                <td>{getNaatKhawanName(audio.naatKhawanId)}</td>
                <td>{typeof audio.duration === 'number' ? `${Math.floor(audio.duration / 60)}:${(audio.duration % 60).toString().padStart(2, '0')}` : (audio.duration || 'N/A')}</td>
                <td>{audio.playCount || 0}</td>
                <td>
                  <div className="admin-actions">
                    <button
                      className="admin-action-btn admin-action-edit"
                      onClick={() => openEditModal(audio)}
                      title="Edit"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      className="admin-action-btn admin-action-delete"
                      onClick={() => handleDelete(audio)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="admin-pagination"
        />
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{modalMode === 'add' ? 'Add New Naat' : 'Edit Naat'}</h3>
              <button
                className="admin-modal-close"
                onClick={closeModal}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="admin-modal-form">
              <div className="admin-form-group">
                <label>Title (English)</label>
                <input
                  type="text"
                  value={audioForm.title}
                  onChange={(e) => setAudioForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="admin-form-group">
                <div className="admin-form-label-row">
                  <label>Title (Urdu)</label>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    disabled={!audioForm.title.trim() || translating}
                    className="admin-translate-btn"
                    title="Auto-translate English title to Urdu"
                  >
                    {translating ? '⏳' : '🔄'} Auto Translate
                  </button>
                </div>
                <input
                  type="text"
                  value={audioForm.urduTitle}
                  onChange={(e) => setAudioForm(prev => ({ ...prev, urduTitle: e.target.value }))}
                  dir="rtl"
                  placeholder="اردو میں نعت کا عنوان"
                />
              </div>

              <div className="admin-form-group">
                <label>NaatKhawan</label>
                <select
                  value={audioForm.naatKhawanId}
                  onChange={(e) => setAudioForm(prev => ({ ...prev, naatKhawanId: Number(e.target.value) }))}
                  required
                >
                  <option value={0}>Select NaatKhawan</option>
                  {naatKhawans.map(nk => (
                    <option key={nk.id} value={nk.id}>{nk.name}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Duration {(modalMode === 'add' && (uploadMethod === 'file' || uploadMethod === 'fetch')) && <span style={{ fontSize: '0.85rem', color: '#666' }}>(auto-calculated)</span>}</label>
                  <input
                    type="text"
                    placeholder={(modalMode === 'add' && (uploadMethod === 'file' || uploadMethod === 'fetch')) ? "Will be calculated automatically" : "e.g., 4:35 or 275 (seconds)"}
                    value={audioForm.duration}
                    onChange={(e) => setAudioForm(prev => ({ ...prev, duration: e.target.value }))}
                    disabled={modalMode === 'add' && (uploadMethod === 'file' || uploadMethod === 'fetch')}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Play Count</label>
                  <input
                    type="number"
                    min="0"
                    value={audioForm.playCount}
                    onChange={(e) => setAudioForm(prev => ({ ...prev, playCount: Number(e.target.value) }))}
                  />
                </div>
              </div>

              {modalMode === 'add' && (
                <div className="admin-upload-section">
                  <div className="admin-upload-method">
                    <button
                      type="button"
                      className={`admin-upload-method-btn ${uploadMethod === 'file' ? 'active' : ''}`}
                      onClick={() => setUploadMethod('file')}
                    >
                      <Upload size={16} />
                      Upload File
                    </button>
                    <button
                      type="button"
                      className={`admin-upload-method-btn ${uploadMethod === 'url' ? 'active' : ''}`}
                      onClick={() => setUploadMethod('url')}
                    >
                      <LinkIcon size={16} />
                      Use URL/Path
                    </button>
                    <button
                      type="button"
                      className={`admin-upload-method-btn ${uploadMethod === 'fetch' ? 'active' : ''}`}
                      onClick={() => setUploadMethod('fetch')}
                    >
                      <CloudDownload size={16} />
                      Fetch from URL
                    </button>
                  </div>

                  {uploadMethod === 'file' ? (
                    <div className="admin-form-group">
                      <label>Upload Audio File</label>
                      <input
                        type="file"
                        accept="audio/mp3,audio/mpeg,audio/*"
                        onChange={handleFileChange}
                        required={modalMode === 'add'}
                      />
                      {audioForm.file && (
                        <p className="file-info" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                          Selected: {audioForm.file.name} ({(audioForm.file.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                  ) : uploadMethod === 'fetch' ? (
                    <div className="admin-form-group">
                      <label>URL to Fetch (will download and save locally)</label>
                      <input
                        type="text"
                        placeholder="https://example.com/audio.mp3"
                        value={audioForm.filePath}
                        onChange={(e) => setAudioForm(prev => ({ ...prev, filePath: e.target.value }))}
                        required
                      />
                      <p className="help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        The file will be downloaded and saved to the server
                      </p>
                    </div>
                  ) : (
                    <div className="admin-form-group">
                      <label>Audio URL/Path (direct link)</label>
                      <input
                        type="text"
                        placeholder="https://example.com/audio.mp3 or /Audio/Naats/MP3/..."
                        value={audioForm.filePath}
                        onChange={(e) => setAudioForm(prev => ({ ...prev, filePath: e.target.value }))}
                        required
                      />
                      <p className="help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                        The URL/path will be saved as-is
                      </p>
                    </div>
                  )}
                </div>
              )}

              {modalMode === 'edit' && (
                <div className="admin-form-group">
                  <label>Audio URL/Path</label>
                  <input
                    type="text"
                    placeholder="https://example.com/audio.mp3 or /Audio/Naats/MP3/..."
                    value={audioForm.filePath}
                    onChange={(e) => setAudioForm(prev => ({ ...prev, filePath: e.target.value }))}
                    required
                  />
                </div>
              )}

              <div className="admin-form-group">
                <div className="admin-form-label-row">
                  <label>Lyrics (Urdu)</label>
                  {modalMode === 'edit' && (
                    <button
                      type="button"
                      onClick={handleTranscribe}
                      disabled={!audioForm.filePath || transcribing}
                      className="admin-translate-btn"
                      title="Transcribe audio to Urdu lyrics"
                    >
                      {transcribing ? '⏳' : '🎤'} Transcribe
                    </button>
                  )}
                </div>
                <textarea
                  value={audioForm.lyrics}
                  onChange={(e) => setAudioForm(prev => ({ ...prev, lyrics: e.target.value }))}
                  dir="rtl"
                  placeholder="نعت کے بول اردو میں"
                  rows={8}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={loading}
                >
                  <Save size={16} />
                  {loading ? (loadingMessage || 'Saving...') : modalMode === 'add' ? 'Add Naat' : 'Update Naat'}
                </button>
              </div>
            </form>

            {/* Loading overlay */}
            {loading && loadingMessage && (
              <div className="admin-loading-overlay" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: '8px'
              }}>
                <div className="loading-spinner" style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #0f4c5c',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '1rem'
                }}></div>
                <p style={{ fontSize: '1rem', color: '#0f4c5c', textAlign: 'center', padding: '0 2rem' }}>
                  {loadingMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        type="danger"
        title="Delete Naat"
        message={`Are you sure you want to delete "${audioToDelete?.title}"? ${audioToDelete?.filePath?.startsWith('/Audio/') ? 'The audio file will also be permanently deleted from the server.' : ''} This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AdminNaats;